import { MasterUploadPart } from '../../../../redux/api/fymAPI.types';
import { MasterPartError } from './masterPartError';

/** How many times a failed part is sent again before the upload stops. */
export const MASTER_PART_RETRIES = 3;

export type UploadedMasterPart = { partNumber: number; etag: string };

/** Lets the hook stop the part that is on the way (the user stops, or the screen closes). */
export class MasterPartsStop {
  stopped = false;

  private current: XMLHttpRequest | null = null;

  follow(xhr: XMLHttpRequest | null): void {
    this.current = xhr;
  }

  stop(): void {
    this.stopped = true;
    this.current?.abort();
  }
}

const STOPPED_ERROR_NAME = 'MasterUploadStopped';

function stoppedError(): Error {
  const error = new Error('The upload stopped');
  error.name = STOPPED_ERROR_NAME;
  return error;
}

export { MasterPartError, type MasterPartFailureKind } from './masterPartError';

/** True when the parts stopped because `MasterPartsStop.stop` ran, not because a part failed. */
export function isStoppedError(error: unknown): boolean {
  return error instanceof Error && error.name === STOPPED_ERROR_NAME;
}

/**
 * Sends one part to its presigned S3 address and resolves with the `ETag` header as S3 sent it.
 * The request has no app headers: the address is the auth, and the app token must not go to S3.
 */
function putPart(
  partNumber: number,
  url: string,
  body: Blob,
  onProgress: (loaded: number) => void,
  stop: MasterPartsStop
) {
  return new Promise<string>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    stop.follow(xhr);
    xhr.open('PUT', url);
    xhr.upload.onprogress = (event) => onProgress(event.loaded);
    xhr.onload = () => {
      const etag = xhr.getResponseHeader('ETag');
      if (xhr.status >= 200 && xhr.status < 300 && etag !== null && etag !== '') {
        resolve(etag);
      } else {
        reject(new MasterPartError('refused', partNumber, xhr.status, `S3 answered ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new MasterPartError('network', partNumber, xhr.status, 'the request did not reach S3'));
    xhr.onabort = () => reject(stoppedError());
    xhr.send(body);
  }).finally(() => stop.follow(null));
}

interface UploadMasterPartsProps {
  file: Blob;
  parts: MasterUploadPart[];
  partSizeBytes: number;
  /** Bytes sent so far: the finished parts and the part on the way. */
  onProgress: (sentBytes: number) => void;
  stop: MasterPartsStop;
}

/**
 * Reads the first byte of a part. A file that moved, changed or is not on the disk fails here
 * with a clear cause; sent as it is, the browser only reports a network error (`net::ERR_FAILED`
 * or `net::ERR_UPLOAD_FILE_CHANGED`).
 */
async function assertReadable(body: Blob, partNumber: number): Promise<void> {
  const head = body.slice(0, 1);
  if (typeof head.arrayBuffer !== 'function') {
    return;
  }
  try {
    await head.arrayBuffer();
  } catch (error) {
    const name = error instanceof Error ? error.name : 'unknown';
    throw new MasterPartError('unreadable', partNumber, 0, `the browser cannot read the file (${name})`);
  }
}

/**
 * Sends the parts one at a time (`file.slice` by `partSizeBytes`). A failed part goes again up to
 * `MASTER_PART_RETRIES` times; after that the promise rejects with a `MasterPartError`. A file that
 * the browser cannot read rejects at once (`unreadable`). It rejects with a stopped error
 * (`isStoppedError`) when `stop.stop()` runs.
 */
export async function uploadMasterParts({
  file,
  parts,
  partSizeBytes,
  onProgress,
  stop,
}: UploadMasterPartsProps): Promise<UploadedMasterPart[]> {
  const sorted = [...parts].sort((a, b) => a.partNumber - b.partNumber);
  const uploaded: UploadedMasterPart[] = [];
  let doneBytes = 0;
  for (const part of sorted) {
    const start = (part.partNumber - 1) * partSizeBytes;
    const body = file.slice(start, Math.min(start + partSizeBytes, file.size));
    const sentBefore = doneBytes;
    // eslint-disable-next-line no-await-in-loop -- one part at a time, see below
    await assertReadable(body, part.partNumber);
    let etag: string | null = null;
    for (let attempt = 0; etag === null; attempt += 1) {
      if (stop.stopped) {
        throw stoppedError();
      }
      try {
        // eslint-disable-next-line no-await-in-loop -- one part at a time keeps the memory and the progress simple
        etag = await putPart(part.partNumber, part.url, body, (loaded) => onProgress(sentBefore + loaded), stop);
      } catch (error) {
        if (isStoppedError(error) || attempt >= MASTER_PART_RETRIES) {
          throw error;
        }
      }
    }
    doneBytes += body.size;
    onProgress(doneBytes);
    uploaded.push({ partNumber: part.partNumber, etag });
  }
  return uploaded;
}
